import { createActions, createReducer } from "reduxsauce"
import { v4 as uuid } from "uuid"
import moment from "moment"
import uploadFiles from "api/file-upload"
import { filter, get, first } from "lodash"
import { takeLatest, select, put, call, getContext } from "redux-saga/effects"
import { post } from "api"
import { Creators as notificationCreators } from "modules/notifications/model"
import { Creators as serverNotificationsCreators } from "modules/server-notifications/model"
import { Creators as dialogCreators } from "modules/dialog/model"
import { Creators as resourcesCreators } from "modules/resources/model"
import { Creators as modelCreators } from "model/data"
import { getSwapDetails } from "./selectors"
import { log } from "helpers/logger"

export const { Types, Creators } = createActions({
  submitStepSuccess: ["step"],
  addFiles: ["files"],
  removeFile: ["file"],
  scanFiles: ["files"],
  scanFilesSuccess: ["result", "uploadedFiles"],
  scanFilesError: [],
  confirmUpload: ["uploadOption"],
  confirmUploadError: [],
  resetUpload: [],
  resetScan: [],
  setModel: ["modelId"],
  changeUploadOption: ["uploadOption"],
}, {
  prefix: "ompnt/modules/swap-positions/",
})

const INITIAL_STATE = {
  activeStep: 0,
  scanResult: null,
  files: [],
  modelId: "",
  uploadedFiles: [],
  uploadOption: "",
  uploadDateTime: null,
}

function handleSubmitStepSuccess(state, step) {
  return { ...state, activeStep: step }
}

function handleAddFiles(state, { files }) {
  const newFiles = files.map(file => ({ id: uuid(), file }))
  return { ...state, files: [...state.files, ...newFiles] }
}

function handleRemoveFile(state, { file: { id } }) {
  return { ...state, files: filter(state.files, file => file.id !== id) }
}

function handleScanFiles(state) {
  return { ...state }
}

function handleScanFilesSuccess(state, { result, uploadedFiles }) {
  return { ...state, activeStep: 1, scanResult: result, uploadDateTime: moment(), uploadedFiles }
}

function handleScanFilesError(state) {
  return { ...state, scanResult: null }
}

function handleConfirmUpload(state) {
  return { ...state }
}

function handleConfirmUploadError(state) {
  return { ...state }
}

function handleResetScan(state) {
  return { ...state, scanResult: null, activeStep: 0, uploadOption: "", uploadDateTime: null }
}

function handleResetUpload(state) {
  return { ...state, scanResult: null, files: [], activeStep: 0, uploadOption: "", uploadDateTime: null, modelId: "" }
}

function handleChangeUploadOption(state, { uploadOption }) {
  return { ...state, uploadOption }
}

function handleSetModel(state, { modelId }) {
  return { ...state, modelId }
}

export const HANDLERS = {
  [Types.SUBMIT_STEP_SUCCESS]: handleSubmitStepSuccess,
  [Types.ADD_FILES]: handleAddFiles,
  [Types.REMOVE_FILE]: handleRemoveFile,
  [Types.SCAN_FILES]: handleScanFiles,
  [Types.SCAN_FILES_SUCCESS]: handleScanFilesSuccess,
  [Types.SCAN_FILES_ERROR]: handleScanFilesError,
  [Types.CONFIRM_UPLOAD]: handleConfirmUpload,
  [Types.CONFIRM_UPLOAD_ERROR]: handleConfirmUploadError,
  [Types.RESET_UPLOAD]: handleResetUpload,
  [Types.RESET_SCAN]: handleResetScan,
  [Types.CHANGE_UPLOAD_OPTION]: handleChangeUploadOption,
  [Types.SET_MODEL]: handleSetModel,
}

export default createReducer(INITIAL_STATE, HANDLERS)

function* scanFiles({ files }) {
  try {
    if (files.length > 0) {
      const { account, swapDetails } = yield select(state => ({
        swapDetails: getSwapDetails(state),
        account: state.global.selected.account,
      }))

      yield put(dialogCreators.open({ title: "Your Files Are Being Scanned", contentText: "Please wait while we scan your data", disableEscapeKeyDown: true, loadingDialog: true, disableBackdropClick: true }))

      // We don't have the actual resource id for baskets
      const resourceAlias = swapDetails.id
      const uploadedFiles = yield call(uploadFiles, account, resourceAlias, files, { type: "basket" })

      if (uploadedFiles) {
        const doctorParams = { filepaths: uploadedFiles, modelId: swapDetails.modelId }
        const doctorURL = `/api/${account}/swaps/${swapDetails.id}/portfolio-doctor`

        const { portfolioDoctorResult } = yield call(post, doctorURL, doctorParams)

        if (!portfolioDoctorResult) {
          yield put(Creators.scanFilesError())
          yield put(notificationCreators.add("error", null, "An unexpected error occurred while scanning the files."))
        } else if (portfolioDoctorResult.errors.length > 0) {
          yield put(Creators.scanFilesError())
          yield put(notificationCreators.add("error", null, get(first(portfolioDoctorResult.errors), "message")))
        } else if (portfolioDoctorResult.data) {
          yield put(Creators.scanFilesSuccess(portfolioDoctorResult.data, uploadedFiles))
          const { navigate } = yield getContext("router")
          yield call(navigate, `/${account}/baskets/${swapDetails.id}/upload/preview`)
        }

        if (portfolioDoctorResult.messages.length > 0) {
          yield put(notificationCreators.add("warning", null, get(first(portfolioDoctorResult.messages), "message")))
        }
      } else {
        yield put(Creators.scanFilesError())
        yield put(notificationCreators.add("error", null, "Something bad happened while uploading the files."))
      }

      yield put(dialogCreators.close())
    }
  } catch (err) {
    let logErr = err
    if (!(err instanceof Error)) {
      logErr = new Error(JSON.stringify(err))
    }

    yield call(log, logErr)
    yield put(Creators.scanFilesError())
    yield put(notificationCreators.add("error", null, "An unexpected error occurred while scanning the files."))
    yield put(dialogCreators.close())
  }
}

function* confirmUpload({ uploadOption }) {
  try {
    const { uploadedFiles, account, swapDetails } = yield select(state => ({
      uploadedFiles: state.modules.swapPositions.uploadedFiles,
      swapDetails: getSwapDetails(state),
      account: state.global.selected.account,
    }))

    yield put(dialogCreators.open({
      title: uploadedFiles.length > 1 ? "Your Files Are Being Uploaded" : "Your File Is Being Uploaded",
      contentText: "Please wait while we upload your data",
      disableEscapeKeyDown: true,
      loadingDialog: true,
      disableBackdropClick: true,
    }))

    const deleteDates = uploadOption === "clear"
    const doctorParams = {
      filepaths: uploadedFiles,
      upload: true,
      modelId: swapDetails.modelId,
      deleteDates,
    }

    const { portfolioDoctorResult } = yield call(post, `/api/${account}/swaps/${swapDetails.id}/portfolio-doctor`, doctorParams)

    if (portfolioDoctorResult !== "OK") {
      yield put(Creators.confirmUploadError())
      yield put(notificationCreators.add("error", null, "An unexpected error occurred while scanning the files."))
      yield put(dialogCreators.close())
    } else {
      yield put(dialogCreators.close())
      yield put(serverNotificationsCreators.open())
      yield put(Creators.resetUpload())
      yield put(modelCreators.resetCollections(["baskets"]))
      yield put(resourcesCreators.addUploadingItem(swapDetails.id))
      const { navigate } = yield getContext("router")
      yield call(navigate, `/${account}/home/resources`)
      // We need to reset the "swap" collection after the redirect to make sure that the module is unmounted and will not
      // reload the data as it's ID and status will have changed
      yield put(modelCreators.resetCollections(["swap"]))
    }
  } catch {
    yield put(Creators.confirmUploadError())
    yield put(notificationCreators.add("error", null, "An unexpected error occurred while uploading the positions."))
    yield put(dialogCreators.close())
  }
}

export function* saga() {
  yield takeLatest(Types.SCAN_FILES, scanFiles)
  yield takeLatest(Types.CONFIRM_UPLOAD, confirmUpload)
}
